import { deliveryStatus } from './delivery';
const id = value => String(value || '').toLowerCase();
const valid = value => /^[a-f\d]{24}$/i.test(String(value || ''));
export const BULK_STORAGE_KEY = 'bulk-full-delivery:v1';

export function bulkIssue(doc) {
  if (!valid(doc?._id) || !['COG', 'The Bengal Craft'].includes(doc.organization)) return 'Invoice identity or organization requires repair.';
  if (!Array.isArray(doc.items) || !doc.items.length) return 'Invoice has no usable items.';
  const ids = doc.items.map(item => id(item?._id));
  if (ids.some(value => !valid(value)) || new Set(ids).size !== ids.length) return 'Persisted, unique item IDs are required.';
  const revision = doc.deliveryRevision !== undefined ? doc.deliveryRevision : doc.__v;
  if (revision !== undefined && (!Number.isSafeInteger(revision) || revision < 0)) return 'Invalid invoice version.';
  if (doc.canManageDelivery === false) return doc.deliveryIssues?.join(' ') || 'Delivery requires manual review.';
  const totals = new Map(ids.map(value => [value, 0])); const requests = new Set(); const transactions = new Set();
  if (doc.deliveryHistory != null && !Array.isArray(doc.deliveryHistory)) return 'Delivery history requires manual repair.';
  for (const row of doc.deliveryHistory || []) {
    if (!row || !totals.has(id(row.itemId)) || !Number.isSafeInteger(row.quantity) || row.quantity <= 0 || (row.requestId && requests.has(row.requestId)) || (row._id && transactions.has(id(row._id)))) return 'Invalid or ambiguous delivery history.';
    totals.set(id(row.itemId), totals.get(id(row.itemId)) + row.quantity);
    if (row.requestId) requests.add(row.requestId); if (row._id) transactions.add(id(row._id));
  }
  if (doc.items.some(item => !Number.isSafeInteger(item.qty) || item.qty < 1 || !Number.isSafeInteger(item.deliveredQty ?? 0) || (item.deliveredQty ?? 0) < (totals.get(id(item._id)) || 0) || (item.deliveredQty ?? 0) > item.qty || (item.deliveredQty ?? 0) < 0)) return 'Ordered/delivered quantities or recorded transaction floors require manual review.';
  if (!Number.isSafeInteger(doc.items.reduce((sum, item) => sum + item.qty, 0))) return 'Quantity total exceeds the supported range.';
  return '';
}
export function bulkSummary(doc) {
  const issue = bulkIssue(doc);
  const items = Array.isArray(doc.items) ? doc.items : [];
  const ordered = items.reduce((sum, item) => sum + (Number.isSafeInteger(item?.qty) ? item.qty : 0), 0);
  const delivered = items.reduce((sum, item) => sum + (Number.isSafeInteger(item?.deliveredQty) ? item.deliveredQty : 0), 0);
  const remaining = ordered - delivered;
  const status = deliveryStatus(doc);
  return { issue, ordered, delivered, remaining, status, eligible: !issue && status !== 'Fully Delivered', lines: items.length };
}
export const deliveryShape = doc => JSON.stringify([doc.organization, doc.items.map(item => [id(item._id), item.qty]).sort(([a], [b]) => a.localeCompare(b))]);
export function actualDeliveryDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
  const [year, month, day] = value.split('-').map(Number); const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date.toISOString() : null;
}
export function saveBulkBatch(batch) {
  // Persist before each write. Storage failure stops the operation, rather than
  // losing the requestId needed to reconcile an uncertain transaction.
  if (batch === null) sessionStorage.removeItem(BULK_STORAGE_KEY);
  else sessionStorage.setItem(BULK_STORAGE_KEY, JSON.stringify(batch));
}
export function readBulkBatch() {
  const raw = sessionStorage.getItem(BULK_STORAGE_KEY);
  if (!raw) return null;
  const batch = JSON.parse(raw);
  if (!batch?.id || !Array.isArray(batch.entries) || !batch.date || !Number.isFinite(Date.parse(batch.date))) throw new Error('Saved batch is invalid; inspect delivery records before proceeding.');
  return batch;
}
